import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCleoStore } from '@/stores/cleoChat';
import { KPN } from '@/types/KPN';
import { InterfaceFilterMultipleSeparator } from '@/types/object_classes/InterfaceListingFilter';
import type { NavigationAction, NavigationOffer } from '@/types/ai/cleoChat';
import type { LocationQueryValue } from 'vue-router';

interface FilterMapEntry {
    urlKey: string;
    implies?: Record<string, string>;
}

// `label` here is a fallback used only when the LLM did not provide one. For Cleo
// navigation offers the label always comes from the offer itself — see mapOfferToAction.
interface NavConfig {
    routeName: string;
    requiredKPN: string[];
    label: string;
    filterMapping: Record<string, FilterMapEntry>;
    paramMapping?: Record<string, string>;
    staticParams?: Record<string, string>;
}

const NAVIGATION_MAP: Record<string, NavConfig> = {
    'domestic-shipment': {
        routeName: 'domestic-shipments',
        requiredKPN: [KPN.DOMESTICROAD_DOMESTICTRANSPORT_ENABLED],
        label: 'View in Domestic Shipments',
        filterMapping: {
            search_all:                 { urlKey: 'search', implies: { search_select: 'search_all' } },
            perm_shipment_id:           { urlKey: 'search', implies: { search_select: 'perm_shipment_id' } },
            temp_shipment_id:           { urlKey: 'search', implies: { search_select: 'temp_shipment_id' } },
            reference:                  { urlKey: 'search', implies: { search_select: 'reference' } },
            financial_reference_1:      { urlKey: 'search', implies: { search_select: 'financial_reference_1' } },
            uit_code:                   { urlKey: 'search', implies: { search_select: 'uit_code' } },
            shipment_status:            { urlKey: 'shipment_status' },
            loading_city_name:          { urlKey: 'loading_city_name' },
            unloading_city_name:        { urlKey: 'unloading_city_name' },
            pickup_address:             { urlKey: 'pickup_address' },
            delivery_address:           { urlKey: 'delivery_address' },
            package_code:               { urlKey: 'package_code' },
            department:                 { urlKey: 'department' },
            has_invoice:                { urlKey: 'has_invoice' },
            has_uit:                    { urlKey: 'has_uit' },
            has_pod:                    { urlKey: 'has_pod' },
            source:                     { urlKey: 'source' },
            unload_delay_kpi:           { urlKey: 'unload_delay_kpi' },
            shipment_date:              { urlKey: 'shipment_date' },
            loading_start_date:         { urlKey: 'loading_start_date' },
            unloading_start_date:       { urlKey: 'unloading_start_date' },
            loading_act_start_date:     { urlKey: 'loading_act_start_date' },
            unloading_act_start_date:   { urlKey: 'unloading_act_start_date' },
        },
    },
    'domestic-shipment-detail': {
        routeName: 'shipment-overview',
        requiredKPN: [KPN.DOMESTICROAD_DOMESTICTRANSPORT_ENABLED],
        label: 'View Shipment',
        paramMapping: { id: 'shipment_id' },
        staticParams: { transport_type: 'domestic' },
        filterMapping: {},
    },
    'international-shipment': {
        routeName: 'international-shipments',
        requiredKPN: [KPN.INTERNATIONALROAD_INTERNATIONALROAD_ENABLED],
        label: 'View in International Shipments',
        filterMapping: {
            search_all:                 { urlKey: 'search', implies: { search_select: 'search_all' } },
            perm_shipment_id:           { urlKey: 'search', implies: { search_select: 'perm_shipment_id' } },
            temp_shipment_id:           { urlKey: 'search', implies: { search_select: 'temp_shipment_id' } },
            reference:                  { urlKey: 'search', implies: { search_select: 'reference' } },
            financial_reference_1:      { urlKey: 'search', implies: { search_select: 'financial_reference_1' } },
            uit_code:                   { urlKey: 'search', implies: { search_select: 'uit_code' } },
            shipment_status:            { urlKey: 'shipment_status' },
            import_export:              { urlKey: 'import_export' },
            loading_country_code:       { urlKey: 'loading_country_code' },
            unloading_country_code:     { urlKey: 'unloading_country_code' },
            loading_city_name:          { urlKey: 'loading_city_name' },
            unloading_city_name:        { urlKey: 'unloading_city_name' },
            package_code:               { urlKey: 'package_code' },
            department:                 { urlKey: 'department' },
            has_invoice:                { urlKey: 'has_invoice' },
            has_uit:                    { urlKey: 'has_uit' },
            has_pod:                    { urlKey: 'has_pod' },
            source:                     { urlKey: 'source' },
            unload_delay_kpi:           { urlKey: 'unload_delay_kpi' },
            shipment_date:              { urlKey: 'shipment_date' },
            loading_start_date:         { urlKey: 'loading_start_date' },
            unloading_start_date:       { urlKey: 'unloading_start_date' },
            loading_act_start_date:     { urlKey: 'loading_act_start_date' },
            unloading_act_start_date:   { urlKey: 'unloading_act_start_date' },
        },
    },
    'international-shipment-detail': {
        routeName: 'shipment-overview',
        requiredKPN: [KPN.INTERNATIONALROAD_INTERNATIONALROAD_ENABLED],
        label: 'View Shipment',
        paramMapping: { id: 'shipment_id' },
        staticParams: { transport_type: 'international' },
        filterMapping: {},
    },
    'sea-shipment': {
        routeName: 'ocean',
        requiredKPN: [KPN.OCEAN_OCEANTRANSPORT_ENABLED],
        label: 'View in Sea Shipments',
        filterMapping: {
            search_all:                 { urlKey: 'search', implies: { search_select: 'search_all' } },
            transport_shipment_code:    { urlKey: 'search', implies: { search_select: 'transport_shipment_code' } },
            shipment_code:              { urlKey: 'search', implies: { search_select: 'transport_shipment_code' } },
            customer_reference:         { urlKey: 'search', implies: { search_select: 'customer_reference' } },
            house_bill_no:              { urlKey: 'search', implies: { search_select: 'house_bill_no' } },
            shipper_name:               { urlKey: 'search', implies: { search_select: 'shipper_name' } },
            delivery:                   { urlKey: 'search', implies: { search_select: 'delivery' } },
            containers:                 { urlKey: 'search', implies: { search_select: 'containers' } },
            invoice_number:             { urlKey: 'search', implies: { search_select: 'invoice_number' } },
            shipment_status:            { urlKey: 'shipment_status' },
            from_country_name:          { urlKey: 'from_country_name' },
            to_country_name:            { urlKey: 'to_country_name' },
            vessel_name:                { urlKey: 'vessel_name' },
            voyage_number:              { urlKey: 'voyage_number' },
            origin_point:               { urlKey: 'origin_point' },
            destination_point:          { urlKey: 'destination_point' },
            secondary_type:             { urlKey: 'secondary_type' },
            type:                       { urlKey: 'type' },
            pack_type:                  { urlKey: 'pack_type' },
            has_invoice:                { urlKey: 'has_invoice' },
            creation_date:              { urlKey: 'creation_date' },
            etd:                        { urlKey: 'etd' },
            atd:                        { urlKey: 'atd' },
            eta:                        { urlKey: 'eta' },
            add:                        { urlKey: 'add' },
            delivered_on:               { urlKey: 'ata' },
            destination_delivery_edd:   { urlKey: 'edd' },
        },
    },
    'air-shipment': {
        routeName: 'air',
        requiredKPN: [KPN.AIR_AIR_ENABLED],
        label: 'View in Air Shipments',
        filterMapping: {
            search_all:                 { urlKey: 'search', implies: { search_select: 'search_all' } },
            transport_shipment_code:    { urlKey: 'search', implies: { search_select: 'transport_shipment_code' } },
            shipment_code:              { urlKey: 'search', implies: { search_select: 'transport_shipment_code' } },
            mawb:                       { urlKey: 'search', implies: { search_select: 'mawb' } },
            customer_reference:         { urlKey: 'search', implies: { search_select: 'customer_reference' } },
            shipper_name:               { urlKey: 'search', implies: { search_select: 'shipper_name' } },
            delivery:                   { urlKey: 'search', implies: { search_select: 'delivery' } },
            containers:                 { urlKey: 'search', implies: { search_select: 'containers' } },
            invoice_number:             { urlKey: 'search', implies: { search_select: 'invoice_number' } },
            shipment_status:            { urlKey: 'shipment_status' },
            from_country_name:          { urlKey: 'from_country_name' },
            to_country_name:            { urlKey: 'to_country_name' },
            origin_point:               { urlKey: 'origin_point' },
            destination_point:          { urlKey: 'destination_point' },
            flight:                     { urlKey: 'flight' },
            secondary_type:             { urlKey: 'secondary_type' },
            pack_type:                  { urlKey: 'pack_type' },
            has_invoice:                { urlKey: 'has_invoice' },
            creation_date:              { urlKey: 'creation_date' },
            etd:                        { urlKey: 'etd' },
            atd:                        { urlKey: 'atd' },
            eta:                        { urlKey: 'eta' },
            add:                        { urlKey: 'add' },
            delivered_on:               { urlKey: 'to_ata' },
            destination_delivery_edd:   { urlKey: 'edd' },
        },
    },
    'strongo-shipment': {
        routeName: 'strongo-shipments',
        requiredKPN: [KPN.STRONGO_STRONGO_ENABLED],
        label: 'View in Strongo Shipments',
        filterMapping: {
            search_all:                 { urlKey: 'search', implies: { search_select: 'search_all' } },
            no:                         { urlKey: 'search', implies: { search_select: 'no' } },
            customer_reference:         { urlKey: 'search', implies: { search_select: 'customer_reference' } },
            invoice_number:             { urlKey: 'search', implies: { search_select: 'invoice_number' } },
            status:                     { urlKey: 'status' },
            from_city:                  { urlKey: 'from_city' },
            from_county:                { urlKey: 'from_county' },
            from_address:               { urlKey: 'from_address' },
            to_city:                    { urlKey: 'to_city' },
            to_county:                  { urlKey: 'to_county' },
            to_address:                 { urlKey: 'to_address' },
            has_invoice:                { urlKey: 'has_invoice' },
            from_time:                  { urlKey: 'from_time' },
            to_time:                    { urlKey: 'to_time' },
            scheduled_pickup_date:      { urlKey: 'scheduled_pickup_date' },
            pickup_date:                { urlKey: 'pickup_date' },
            last_status_date:           { urlKey: 'last_status_date' },
        },
    },
    'strongo-shipment-detail': {
        routeName: 'strongo-shipment-overview',
        requiredKPN: [KPN.STRONGO_STRONGO_ENABLED],
        label: 'View Strongo Shipment',
        paramMapping: { id: 'shipment_id' },
        filterMapping: {},
    },
    'simplify-inbound': {
        routeName: 'inbounds',
        requiredKPN: [KPN.WAREHOUSINGSIMPLIFY_WAREHOUSINGSIMPLIFYRECEPTIONRETURN_ENABLED],
        label: 'View in Inbounds',
        filterMapping: {
            search_all:                  { urlKey: 'search', implies: { search_select: 'search_all' } },
            id_document:                 { urlKey: 'search', implies: { search_select: 'id_document' } },
            id_receptie_wms:             { urlKey: 'search', implies: { search_select: 'id_receptie_wms' } },
            status:                      { urlKey: 'status' },
            has_contract:                { urlKey: 'has_contract' },
            creation_date:               { urlKey: 'creation_date' },
            data_preconizata_receptie:   { urlKey: 'data_preconizata_receptie' },
        },
    },
    'simplify-outbound': {
        routeName: 'outbounds',
        requiredKPN: [KPN.WAREHOUSINGSIMPLIFY_WAREHOUSINGSIMPLIFYORDER_ENABLED],
        label: 'View in Outbounds',
        filterMapping: {
            search_all:                  { urlKey: 'search', implies: { search_select: 'search_all' } },
            id_comanda:                  { urlKey: 'search', implies: { search_select: 'id_comanda' } },
            id_livrare_wms:              { urlKey: 'search', implies: { search_select: 'id_livrare_wms' } },
            status:                      { urlKey: 'status' },
            has_contract:                { urlKey: 'has_contract' },
            tara_livrare:                { urlKey: 'tara_livrare' },
            shipper:                     { urlKey: 'shipper' },
            creation_date:               { urlKey: 'creation_date' },
            data_livrare:                { urlKey: 'data_livrare' },
        },
    },
    'vbs-inbound': {
        routeName: 'vbs-inbounds',
        requiredKPN: [KPN.WAREHOUSING_WAREHOUSINGRECEPTIONRETURN_ENABLED],
        label: 'View in VBS Inbounds',
        filterMapping: {
            search_all:                  { urlKey: 'search', implies: { search_select: 'search_all' } },
            document_no_client:          { urlKey: 'search', implies: { search_select: 'document_no_client' } },
            reception_no_wms:            { urlKey: 'search', implies: { search_select: 'reception_no_wms' } },
            supplier_name:               { urlKey: 'search', implies: { search_select: 'supplier_name' } },
            reception_status:            { urlKey: 'reception_status' },
            has_contract:                { urlKey: 'has_contract' },
            oneclick_upload:             { urlKey: 'oneclick_upload' },
            creation_date:               { urlKey: 'creation_date' },
            delivery_date:               { urlKey: 'delivery_date' },
        },
    },
    'vbs-outbound': {
        routeName: 'vbs-outbounds',
        requiredKPN: [KPN.WAREHOUSING_WAREHOUSINGORDERS_ENABLED],
        label: 'View in VBS Outbounds',
        filterMapping: {
            search_all:                  { urlKey: 'search', implies: { search_select: 'search_all' } },
            order_no_erp:                { urlKey: 'search', implies: { search_select: 'order_no_erp' } },
            order_no_vbs:                { urlKey: 'search', implies: { search_select: 'order_no_vbs' } },
            order_status:                { urlKey: 'order_status' },
            has_contract:                { urlKey: 'has_contract' },
            oneclick_upload:             { urlKey: 'oneclick_upload' },
            county:                      { urlKey: 'county' },
            ship:                        { urlKey: 'ship' },
            creation_date:               { urlKey: 'creation_date' },
            delivery_date:               { urlKey: 'delivery_date' },
        },
    },
    'customer-invoice': {
        routeName: 'customers',
        requiredKPN: [KPN.ERP_INVOICES_ENABLED],
        label: 'View in Invoices',
        filterMapping: {
            document_no:                 { urlKey: 'document_no' },
            currency_code:               { urlKey: 'currency_code' },
            document_type:               { urlKey: 'document_type' },
            remaining_amount:            { urlKey: 'remaining_amount' },
            remaining_amt_lcy:           { urlKey: 'remaining_amt_lcy' },
            overdue:                     { urlKey: 'overdue' },
            invoice:                     { urlKey: 'invoice' },
            invoice_details:             { urlKey: 'invoice_details' },
            service_type:                { urlKey: 'service_type' },
            document_date:               { urlKey: 'document_date' },
            due_date:                    { urlKey: 'due_date' },
        },
    },
    'customer-invoice-detail': {
        routeName: 'customer-invoice-details',
        requiredKPN: [KPN.ERP_INVOICES_ENABLED, KPN.ERP_CUSTOMERINVOICEDETAILS_ENABLED],
        label: 'View Invoice',
        paramMapping: { entry_no: 'entry_no' },
        filterMapping: {},
    },
    'company-profile': {
        routeName: 'company-profile',
        requiredKPN: [KPN.MANAGE_COMPANYPROFILE_ENABLED],
        label: 'View Company Profile',
        filterMapping: {},
    },
    'claims': {
        routeName: 'claims',
        requiredKPN: [KPN.CLAIMS_CLAIMS_ENABLED],
        label: 'View Claims & Support',
        filterMapping: {},
    },
};

interface ResourceMapping {
    listing: string;
    detail?: string;
    detailKey?: string;
}

const JSON_API_TO_RESOURCE_TYPE: Record<string, ResourceMapping> = {
    'domestic-shipments':              { listing: 'domestic-shipment',        detail: 'domestic-shipment-detail',      detailKey: 'id' },
    'international-shipments':         { listing: 'international-shipment',   detail: 'international-shipment-detail', detailKey: 'id' },
    'tms-sea-shipments':               { listing: 'sea-shipment' },
    'tms-air-shipments':               { listing: 'air-shipment' },
    'strongo-shipments':               { listing: 'strongo-shipment',         detail: 'strongo-shipment-detail',       detailKey: 'id' },
    'inbounds':                        { listing: 'simplify-inbound' },
    'outbounds':                       { listing: 'simplify-outbound' },
    'vbs-report-inbounds':             { listing: 'vbs-inbound' },
    'vbs-report-outbounds':            { listing: 'vbs-outbound' },
    'customer-ledger-entries':         { listing: 'customer-invoice',         detail: 'customer-invoice-detail',       detailKey: 'entry_no' },
    'invoices':                        { listing: 'customer-invoice' },
    'companies':                       { listing: 'company-profile' },
    'claims':                          { listing: 'claims' },
};

type QueryValue = LocationQueryValue | LocationQueryValue[];

const DATE_RANGE_LONG_RE  = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})\s*,\s*(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/;
const DATE_RANGE_SHORT_RE = /^(\d{4}-\d{2}-\d{2})\s*,\s*(\d{4}-\d{2}-\d{2})$/;

// Accepts "YYYY-MM-DD,YYYY-MM-DD" (full-day, defaults to 00:00:00 / 23:59:59 local)
// or legacy "YYYY-MM-DD HH:MM:SS, YYYY-MM-DD HH:MM:SS".
const parseDateRangeForUrl = (value: string): [string, string] | null => {
    const longMatch = DATE_RANGE_LONG_RE.exec(value);
    if (longMatch) {
        const [, startDate, startTime, endDate, endTime] = longMatch;
        const startISO = new Date(`${startDate}T${startTime}`).toISOString();
        const endISO   = new Date(new Date(`${endDate}T${endTime}`).getTime() + 999).toISOString();
        return [startISO, endISO];
    }

    const shortMatch = DATE_RANGE_SHORT_RE.exec(value);
    if (shortMatch) {
        const [, startDate, endDate] = shortMatch;
        const [sy, sm, sd] = startDate.split('-').map(Number);
        const [ey, em, ed] = endDate.split('-').map(Number);
        const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
        const end   = new Date(ey, em - 1, ed, 23, 59, 59, 999);
        return [start.toISOString(), end.toISOString()];
    }

    return null;
};

// `has_*` boolean filters in this codebase are consistently 'yes' / 'no' strings.
// The agent's plugin descriptions occasionally surface them as true/false, so
// normalize defensively. Other boolean-shaped filters (`invoice`, `oneclick_upload`)
// genuinely use 'true' / 'false' on both schema and listing — do NOT normalize those.
const normalizeFilterValue = (agentKey: string, value: string): string => {
    if (!agentKey.startsWith('has_')) return value;
    const lower = value.toLowerCase();
    if (lower === 'true')  return 'yes';
    if (lower === 'false') return 'no';
    return value;
};

const buildQuery = (config: NavConfig, filters: Record<string, string>): Record<string, QueryValue> => {
    const query: Record<string, QueryValue> = {};

    for (const [agentKey, entry] of Object.entries(config.filterMapping)) {
        const rawValue = filters[agentKey];
        if (!rawValue) continue;

        const value = normalizeFilterValue(agentKey, rawValue);

        const range = parseDateRangeForUrl(value);
        if (range) {
            query[entry.urlKey]            = 'custom-range';
            query[entry.urlKey + '_range'] = range;
        } else if (value.includes(InterfaceFilterMultipleSeparator.SEPARATOR)) {
            query[entry.urlKey] = value.split(InterfaceFilterMultipleSeparator.SEPARATOR).map(part => normalizeFilterValue(agentKey, part));
        } else {
            query[entry.urlKey] = value;
        }

        if (entry.implies) {
            for (const [impliedKey, impliedVal] of Object.entries(entry.implies)) {
                if (!(impliedKey in query)) {
                    query[impliedKey] = impliedVal;
                }
            }
        }
    }

    return query;
};

const buildParams = (config: NavConfig, filters: Record<string, string>): Record<string, string> => {
    const params: Record<string, string> = { ...(config.staticParams ?? {}) };

    for (const [agentKey, paramName] of Object.entries(config.paramMapping ?? {})) {
        const value = filters[agentKey];
        if (value && !(paramName in params)) {
            params[paramName] = value;
        }
    }

    return params;
};

// Resolves one orchestrator-declared offer into a concrete NavigationAction.
// The label normally comes from the offer (LLM-written, in the user's language).
// If the LLM forgets to provide a label, fall back to NAVIGATION_MAP's static label.
const mapOfferToAction = (offer: NavigationOffer | undefined | null): NavigationAction | null => {
    if (!offer || !offer.resource) return null;

    const mapping = JSON_API_TO_RESOURCE_TYPE[offer.resource];
    if (!mapping) return null;

    const filters = offer.filters ?? {};
    let route = mapping.listing;
    if (mapping.detail && mapping.detailKey && filters[mapping.detailKey]) {
        route = mapping.detail;
    }

    const config = NAVIGATION_MAP[route];
    if (!config) return null;

    const label = (offer.label && offer.label.trim()) || config.label;
    return { type: 'navigate', label, route, filters };
};

export const mapOffersToActions = (offers: NavigationOffer[] | undefined | null): NavigationAction[] => {
    if (!offers || offers.length === 0) return [];
    const actions: NavigationAction[] = [];
    for (const offer of offers) {
        const action = mapOfferToAction(offer);
        if (action) actions.push(action);
    }
    return actions;
};

const detectConversationLanguage = (text: string | undefined | null): 'ro' | 'en' => {
    if (!text) return 'en';
    if (/[ăâîșțĂÂÎȘȚ]/.test(text)) return 'ro';
    const roWords = /\b(și|si|sau|este|sunt|care|pentru|ce|cum|câte|cate|cât|cat|vreau|arată|arata|transporturi|facturi|comenzi|companie|companii|curse|livrare|unde|când|cand|mulțumesc|multumesc)\b/i;
    return roWords.test(text) ? 'ro' : 'en';
};

export const getNavigationHeading = (text: string | undefined | null): string => {
    return detectConversationLanguage(text) === 'ro'
        ? 'Vrei să te duc la una dintre aceste pagini?'
        : 'Would you like me to take you to one of these pages?';
};

export const useNavigationActions = () => {
    const router    = useRouter();
    const authStore = useAuthStore();
    const cleoStore = useCleoStore();

    const getConfig = (action: NavigationAction): NavConfig | undefined => {
        return NAVIGATION_MAP[action.route];
    };

    const hasAccess = (action: NavigationAction): boolean => {
        const config = getConfig(action);
        if (!config) return false;
        return authStore.accountPermissions.allowedAll(config.requiredKPN);
    };

    const isOnTargetRoute = (action: NavigationAction): boolean => {
        const config = getConfig(action);
        if (!config) return false;
        return router.currentRoute.value.name === config.routeName;
    };

    const navigate = async (action: NavigationAction): Promise<void> => {
        const config = getConfig(action);
        if (!config) return;
        const sameRoute = isOnTargetRoute(action);
        await router.push({
            name:   config.routeName,
            params: buildParams(config, action.filters),
            query:  buildQuery(config, action.filters),
        });
        if (sameRoute) {
            cleoStore.bumpViewReloadKey();
        }
    };

    const getHref = (action: NavigationAction): string | null => {
        const config = getConfig(action);
        if (!config) return null;
        return router.resolve({
            name:   config.routeName,
            params: buildParams(config, action.filters),
            query:  buildQuery(config, action.filters),
        }).href;
    };

    return { hasAccess, navigate, getHref };
};
